import { MAX_WAVES, getWaveDuration } from "../data/constants"
import {
  getSectorForWave,
  getWaveSpawnWeight,
  isBossWave,
} from "../data/sectors"
import type { SectorDefinition } from "../data/sectors"
import type { EnemyType } from "../types"
import { waveStrategy } from "./wave-strategy"

const WAVE_TUNING = {
  rewardWaveStep: 0.03,
  spawnIntervalBaseMs: 3200,
} as const

interface WaveSnapshot {
  currentWave: number
  waveTimeElapsed: number
  waveProgress: number
  isWaveActive: boolean
}

export class WaveTimeline {
  private currentSector: SectorDefinition = getSectorForWave(1)

  private snapshot: WaveSnapshot = {
    currentWave: 1,
    waveTimeElapsed: 0,
    waveProgress: 0,
    isWaveActive: true,
  }

  update(deltaTime: number): boolean {
    if (!this.snapshot.isWaveActive) return false

    if (this.isBossWave()) {
      this.snapshot.waveTimeElapsed += deltaTime
      return false
    }

    const waveDuration = this.getCurrentWaveDuration()
    this.snapshot.waveTimeElapsed += deltaTime
    this.snapshot.waveProgress = Math.min(
      1,
      this.snapshot.waveTimeElapsed / waveDuration,
    )

    if (this.snapshot.waveTimeElapsed >= waveDuration) {
      return this.completeWave()
    }

    return false
  }

  completeBossWave(): boolean {
    if (!this.isBossWave()) return false
    return this.completeWave()
  }

  advanceWave(): void {
    if (this.snapshot.currentWave >= MAX_WAVES) {
      this.snapshot.isWaveActive = false
      return
    }

    this.snapshot.currentWave++
    this.currentSector = getSectorForWave(this.snapshot.currentWave)
    this.snapshot.waveTimeElapsed = 0
    this.snapshot.waveProgress = 0
    this.snapshot.isWaveActive = true
  }

  reset(wave: number): void {
    this.snapshot.currentWave = wave
    this.currentSector = getSectorForWave(wave)
    this.snapshot.waveTimeElapsed = 0
    this.snapshot.waveProgress = 0
    this.snapshot.isWaveActive = true
  }

  private completeWave(): boolean {
    if (!this.snapshot.isWaveActive) return false
    this.snapshot.isWaveActive = false
    return true
  }

  getWave(): number {
    return this.snapshot.currentWave
  }

  getWaveProgress(): number {
    return this.snapshot.waveProgress
  }

  isBossWave(): boolean {
    return isBossWave(this.snapshot.currentWave)
  }

  getCurrentWaveDuration(): number {
    return getWaveDuration(this.snapshot.currentWave)
  }

  getCurrentSector(): SectorDefinition {
    return this.currentSector
  }

  getEnemyDifficultyMultiplier(): number {
    return waveStrategy.getEnemyDifficultyMultiplier(this.snapshot.currentWave)
  }

  getRewardMultiplier(): number {
    const waveBonus =
      1 + (this.snapshot.currentWave - 1) * WAVE_TUNING.rewardWaveStep
    return this.currentSector.treatMultiplier * waveBonus
  }

  rollEnemyType(): EnemyType {
    return waveStrategy.rollEnemyType(
      this.snapshot.currentWave,
      this.snapshot.waveProgress,
    )
  }

  getSpawnBatchSize(): number {
    return waveStrategy.getSpawnBatchSize(this.snapshot.currentWave)
  }

  getEnemyPopulationCap(): number {
    return waveStrategy.getEnemyPopulationCap(this.snapshot.currentWave)
  }

  shouldUseEncounter(spawnCycle: number): boolean {
    return waveStrategy.shouldUseEncounter(
      this.snapshot.currentWave,
      spawnCycle,
    )
  }

  getEncounterSize(slotsAvailable: number): number {
    return waveStrategy.getEncounterSize(
      this.snapshot.currentWave,
      slotsAvailable,
    )
  }

  getEncounterGroup(_slots: number): EnemyType[] {
    const size = this.getEncounterSize(3)
    const group: EnemyType[] = []
    for (let i = 0; i < size; i++) {
      group.push(this.rollEnemyType())
    }
    return group
  }

  getSpawnIntervalMs(): number {
    const base = WAVE_TUNING.spawnIntervalBaseMs
    const weight = getWaveSpawnWeight(
      this.snapshot.currentWave,
      this.snapshot.waveProgress,
    )
    // weight is 1.0 by default in existing config, sum of all spawnRates.
    // If user sets smaller rates (like 0.2 + 0.3 = 0.5), spawns slow down.
    return base / Math.max(0.1, weight)
  }

  getRemainingTime(): number {
    return Math.max(
      0,
      this.getCurrentWaveDuration() - this.snapshot.waveTimeElapsed,
    )
  }

  getElapsedTime(): number {
    return this.snapshot.waveTimeElapsed
  }

  isGameWon(): boolean {
    return this.snapshot.currentWave >= MAX_WAVES && !this.snapshot.isWaveActive
  }

  reportGathererEscape(): void {
    // Optional: could track gatherer escapes for scoring
  }
}
